import {
  AlertTriangle,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle,
  MapPin,
  Plane,
  Save,
  Syringe,
  Users,
  X,
} from "lucide-react";
import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { CompanionManager } from "../../components/travels/CompanionManager";
import { SegmentInlineList } from "../../components/travels/SegmentInlineList";
import { AdvisoryChangeLog } from "../../components/travels/AdvisoryChangeLog";
import { DoctorSummarySender } from "../../components/travels/DoctorSummarySender";
import { SubscriptionGate } from "../../components/travels/SubscriptionGate";
import { TripHealthUpsell } from "../../components/travels/TripHealthUpsell";
import { PartnerOffersSection } from "../../components/marketplace/PartnerOffersSection";
import { CountryAutocomplete } from "../../components/ui/CountryAutocomplete";
import { DestinationAutocomplete } from "../../components/ui/DestinationAutocomplete";
import { NudgeBanner } from "../../components/ui/NudgeBanner";
import { PageBanner } from "../../components/ui/PageBanner";
import {
  getHealthRequirements,
  getHealthWarnings,
  HealthRequirements,
  HealthWarning,
} from "../../services/api/travelHealth";
import {
  createTravel,
  getSegments,
  getTravel,
  Travel,
  TravelSegment,
  updateTravel,
} from "../../services/api/travels";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import { useSubscriptionStore } from "../../stores/subscriptionStore";
import { paymentRequired } from "@/utils/paymentRequired";
import { log } from "@/lib/log";

/**
 * The edit form's own shape. Deriving the unions from Travel keeps the two in
 * step; inferring them from the initial object narrowed `purpose` and
 * `tripType` to single literals, which is what the `as Record<string, unknown>`
 * casts at the change handlers were working around.
 */
interface TravelFormData {
  tripTitle: string;
  destination: string;
  country: string;
  departureDate: string;
  returnDate: string;
  purpose: string;
  tripType: NonNullable<Travel["tripType"]>;
  status: Travel["status"];
  statusMode: NonNullable<Travel["statusMode"]>;
  tripOperator: string;
  bookingReference: string;
  startLocation: string;
  endLocation: string;
  companions: number;
  notes: string;
  activities: string[];
}

const TripMapLazy = lazy(() =>
  import("../../components/travels/TripMap").then((m) => ({
    default: m.TripMap,
  })),
);

export const TravelDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const isEditMode = id !== "new";
  // Managing a trip is free; its health section is sold with PLUS. Known to
  // be below PLUS: do not ask. Not known: ask quietly, and a 402 shows the
  // offer in place of the section rather than a dialog over the whole page.
  const { currentSubscription, hasAccess } = useSubscriptionStore();
  const knownNotEntitled = currentSubscription !== null && !hasAccess("PLUS");
  const [healthPaywalled, setHealthPaywalled] = useState(false);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [healthData, setHealthData] = useState<HealthRequirements | null>(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [travelWarnings, setTravelWarnings] = useState<HealthWarning[]>([]);
  const [segments, setSegments] = useState<TravelSegment[]>([]);

  const [formData, setFormData] = useState<TravelFormData>({
    tripTitle: "",
    destination: "",
    country: "",
    departureDate: "",
    returnDate: "",
    purpose: "leisure",
    tripType: "other",
    status: "planned",
    statusMode: "automatic",
    tripOperator: "",
    bookingReference: "",
    startLocation: "",
    endLocation: "",
    companions: 0,
    notes: "",
    activities: [],
  });

  useEffect(() => {
    if (isEditMode && id) {
      loadTravel(id);
      loadSegments(id);
    }
  }, [id, isEditMode]);

  const loadTravel = async (travelId: string) => {
    try {
      setLoading(true);
      log.debug("🔍 Loading travel with ID:", travelId);
      const travel = await getTravel(travelId);
      log.debug("✅ Travel loaded successfully:", travel);
      setFormData({
        tripTitle: travel.tripTitle || travel.destination || "",
        destination: travel.destination || "",
        country: travel.country || "",
        departureDate:
          (travel.departureDate ? travel.departureDate.split("T")[0] : "") ||
          "",
        returnDate:
          (travel.returnDate ? travel.returnDate.split("T")[0] : "") || "",
        purpose: travel.purpose || "leisure",
        tripType: travel.tripType || "other",
        status: travel.status || "planned",
        statusMode: travel.statusMode || "automatic",
        tripOperator: travel.tripOperator || "",
        bookingReference: travel.bookingReference || "",
        startLocation: travel.startLocation || "",
        endLocation: travel.endLocation || "",
        companions: travel.companions || 0,
        notes: travel.notes || "",
        activities: travel.activities || [],
      });
    } catch (error: any) {
      console.error("❌ Failed to load travel:", error);
      // Use mock data if API fails
      setFormData({
        tripTitle: "",
        destination: "Unknown Destination",
        country: "Unknown",
        departureDate: new Date().toISOString().split("T")[0] || "",
        returnDate: new Date().toISOString().split("T")[0] || "",
        purpose: "leisure",
        tripType: "other",
        status: "planned",
        statusMode: "automatic" as const,
        tripOperator: "",
        bookingReference: "",
        startLocation: "",
        endLocation: "",
        companions: 0,
        notes: "",
        activities: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSegments = async (travelId: string) => {
    try {
      const segmentData = await getSegments(travelId);
      setSegments(segmentData);
    } catch (error: any) {
      console.error("Failed to load segments:", error);
      setSegments([]);
    }
  };

  const loadHealthRequirements = useCallback(
    async (country: string, departureDate?: string) => {
      try {
        setLoadingHealth(true);
        setHealthPaywalled(false);
        const [data, warnings] = await Promise.all([
          getHealthRequirements(country, departureDate, { quietPaywall: true }),
          getHealthWarnings(country, { quietPaywall: true }).catch(
            () => [] as HealthWarning[],
          ),
        ]);
        setHealthData(data);
        setTravelWarnings(
          warnings.filter((w) => w.level === "high" || w.level === "medium"),
        );
      } catch (error: unknown) {
        if (paymentRequired(error)) {
          setHealthPaywalled(true);
        } else {
          console.error("Failed to load health requirements:", error);
        }
        setHealthData(null);
        setTravelWarnings([]);
      } finally {
        setLoadingHealth(false);
      }
    },
    [],
  );

  // Load health requirements when country or departure date changes
  useEffect(() => {
    const country = formData.country?.trim();

    if (!country || country.length <= 2 || knownNotEntitled) {
      setHealthData(null);
      setTravelWarnings([]);
      return;
    }

    const timer = window.setTimeout(() => {
      loadHealthRequirements(country, formData.departureDate);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [
    formData.country,
    formData.departureDate,
    loadHealthRequirements,
    knownNotEntitled,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.destination.trim()) {
      toast.error(t.destination + " " + t.required.toLowerCase());
      return;
    }
    if (!formData.country.trim()) {
      toast.error(t.country + " " + t.required.toLowerCase());
      return;
    }
    if (!formData.departureDate) {
      toast.error(t.departureDate + " " + t.required.toLowerCase());
      return;
    }
    if (!formData.returnDate) {
      toast.error(t.returnDate + " " + t.required.toLowerCase());
      return;
    }

    // Date validation
    const departure = new Date(formData.departureDate);
    const returnDate = new Date(formData.returnDate);
    if (returnDate < departure) {
      toast.error("Return date must be after departure date");
      return;
    }

    try {
      setSaving(true);
      const travelData = {
        ...formData,
        departureDate: new Date(formData.departureDate).toISOString(),
        returnDate: new Date(formData.returnDate).toISOString(),
      };

      if (isEditMode && id) {
        await updateTravel(id, travelData);
        toast.success(t.savedSuccessfully);
      } else {
        await createTravel(travelData);
        toast.success("Travel created successfully");
      }
      navigate("/traveler/travels");
    } catch (error: any) {
      console.error("Failed to save travel:", error);
      toast.error("Failed to save travel");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/traveler/travels");
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <PageBanner
          icon={Plane}
          title={t.travels}
          description={t.planYourTrips}
        />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t.loading}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Banner */}
      <PageBanner
        icon={Plane}
        title={isEditMode ? t.editTravel : t.newTravel}
        description={
          isEditMode ? "Update your travel details" : "Plan your next adventure"
        }
      />

      {/* Active Travel Warnings Banner */}
      {travelWarnings.length > 0 && (
        <div className="rounded-xl border border-orange-300 bg-orange-50 p-4 space-y-2">
          <div className="flex items-center gap-2 font-semibold text-orange-800">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            {t.travelWarnings ?? "Travel Warnings"} ({formData.country})
          </div>
          <ul className="space-y-1">
            {travelWarnings.map((w, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-orange-700"
              >
                <span
                  className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-xs font-bold uppercase ${w.level === "high" ? "bg-red-200 text-red-800" : "bg-yellow-200 text-yellow-800"}`}
                >
                  {w.level}
                </span>
                {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary-600" />
              {t.travelDestination}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.tripTitle || "Trip Title"}
                </label>
                <input
                  type="text"
                  value={formData.tripTitle}
                  onChange={(e) =>
                    setFormData({ ...formData, tripTitle: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Summer Vacation 2026"
                />
              </div>
              <div>
                <DestinationAutocomplete
                  city={formData.destination}
                  country={formData.country}
                  onCityChange={(val) =>
                    setFormData({ ...formData, destination: val })
                  }
                  onCountryChange={(val) =>
                    setFormData({ ...formData, country: val })
                  }
                  label={`${t.destination} *`}
                  placeholder="e.g., Barcelona"
                  required
                />
              </div>
              <CountryAutocomplete
                value={formData.country}
                onChange={(value) =>
                  setFormData({ ...formData, country: value })
                }
                label={t.country}
                placeholder="e.g., Spain"
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.startLocation || "Start Location"}
                </label>
                <input
                  type="text"
                  value={formData.startLocation}
                  onChange={(e) =>
                    setFormData({ ...formData, startLocation: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Munich Airport"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.endLocation || "End Location"}
                </label>
                <input
                  type="text"
                  value={formData.endLocation}
                  onChange={(e) =>
                    setFormData({ ...formData, endLocation: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Munich Airport"
                />
              </div>
            </div>
          </div>

          {/* Dates */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary-600" />
              {t.travelDates || "Travel Dates"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.departureDate} *
                </label>
                <input
                  type="date"
                  value={formData.departureDate}
                  onChange={(e) =>
                    setFormData({ ...formData, departureDate: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.returnDate} *
                </label>
                <input
                  type="date"
                  value={formData.returnDate}
                  onChange={(e) =>
                    setFormData({ ...formData, returnDate: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Purpose & Companions */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary-600" />
              {t.travelDetails || "Travel Details"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.travelPurpose}
                </label>
                <select
                  value={formData.purpose}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      purpose: e.target.value,
                    })
                  }
                  className="w-full h-[42px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="leisure">{t.purposeLeisure}</option>
                  <option value="business">{t.purposeBusiness}</option>
                  <option value="medical">{t.purposeMedical}</option>
                  <option value="education">{t.purposeEducation}</option>
                  <option value="volunteering">{t.purposeVolunteering}</option>
                  <option value="other">{t.purposeOther}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.tripType || "Trip Type"}
                </label>
                <select
                  value={formData.tripType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tripType: e.target.value as TravelFormData["tripType"],
                    })
                  }
                  className="w-full h-[42px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="cruise">{t.tripTypeCruise || "Cruise"}</option>
                  <option value="beach">{t.tripTypeBeach || "Beach"}</option>
                  <option value="mountains">
                    {t.tripTypeMountains || "Mountains"}
                  </option>
                  <option value="sports">{t.tripTypeSports || "Sports"}</option>
                  <option value="education">
                    {t.tripTypeEducation || "Education"}
                  </option>
                  <option value="event">{t.tripTypeEvent || "Event"}</option>
                  <option value="culture">
                    {t.tripTypeCulture || "Culture"}
                  </option>
                  <option value="expedition">
                    {t.tripTypeExpedition || "Expedition"}
                  </option>
                  <option value="other">{t.purposeOther}</option>
                </select>
              </div>

              {/* AC 8: Planned Activities multi-select */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.plannedActivities ?? "Planned Activities"}
                  <span className="ml-1 text-xs text-gray-400">
                    (for health risk analysis)
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "diving",
                    "climbing",
                    "skiing",
                    "hiking",
                    "biking",
                    "swimming",
                    "safari",
                  ].map((act) => {
                    const checked = (formData.activities ?? []).includes(act);
                    return (
                      <button
                        key={act}
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            activities: checked
                              ? (formData.activities ?? []).filter(
                                  (a) => a !== act,
                                )
                              : [...(formData.activities ?? []), act],
                          })
                        }
                        className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                          checked
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white text-gray-600 border-gray-300 hover:border-primary-400"
                        }`}
                      >
                        {t[`activity_${act}` as keyof typeof t] ??
                          act.charAt(0).toUpperCase() + act.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.tripOperator || "Tour Operator"}
                </label>
                <input
                  type="text"
                  value={formData.tripOperator}
                  onChange={(e) =>
                    setFormData({ ...formData, tripOperator: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., TUI, AIDA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.bookingReference || "Booking Reference"}
                </label>
                <input
                  type="text"
                  value={formData.bookingReference}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      bookingReference: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., ABC123456"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Users className="h-4 w-4" />
                  {t.travelCompanions}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.companions}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      companions: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              {/* Status Override – only shown in edit mode */}
              {isEditMode && (
                <div className="col-span-2 rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-700">
                      {t.travelStatus}
                    </p>
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-500 select-none">
                      <input
                        type="checkbox"
                        checked={formData.statusMode === "manual"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            statusMode: e.target.checked
                              ? "manual"
                              : "automatic",
                          })
                        }
                        className="rounded border-gray-400"
                      />
                      {t.statusModeManual}
                    </label>
                  </div>
                  {formData.statusMode === "automatic" ? (
                    <p className="text-xs text-gray-500">
                      {t.statusModeAutoHint}
                    </p>
                  ) : (
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as TravelFormData["status"],
                        })
                      }
                      className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="planned">
                        {t.statusPlanned || "Planned"}
                      </option>
                      <option value="current">
                        {t.statusCurrent || "Aktuell"}
                      </option>
                      <option value="completed">
                        {t.statusCompleted || "Abgeschlossen"}
                      </option>
                      <option value="cancelled">
                        {t.statusCancelled || "Storniert"}
                      </option>
                      <option value="archived">
                        {t.statusArchived || "Archiviert"}
                      </option>
                    </select>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.notes}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Add any additional information about your travel..."
            />
          </div>

          {/* Segments Section */}
          {isEditMode && id && (
            <div className="border-t border-gray-200 pt-6">
              <SegmentInlineList
                travelId={id}
                segments={segments}
                onSegmentsChange={setSegments}
                canUploadDocuments={false}
              />
            </div>
          )}

          {/* Trip Map */}
          {isEditMode && segments.length > 0 && (
            <div className="border-t border-gray-200 pt-6">
              <SubscriptionGate requiredPlan="PREMIUM">
                <Suspense
                  fallback={
                    <div className="h-56 animate-pulse rounded-xl bg-gray-100" />
                  }
                >
                  <TripMapLazy segments={segments} />
                </Suspense>
              </SubscriptionGate>
            </div>
          )}

          {/* What changed about the destination while this trip stood (REQ-013, P4) */}
          {isEditMode && id && (
            <div className="border-t border-gray-200 pt-6">
              <SubscriptionGate requiredPlan="PREMIUM">
                <AdvisoryChangeLog travelId={id} />
              </SubscriptionGate>
            </div>
          )}

          {/* Trip summary for the traveller's own doctor (REQ-013, P3) */}
          {isEditMode && id && (
            <div className="border-t border-gray-200 pt-6">
              <SubscriptionGate requiredPlan="PREMIUM">
                <DoctorSummarySender travelId={id} />
              </SubscriptionGate>
            </div>
          )}

          {/* Companion Manager */}
          {isEditMode && id && (
            <div className="border-t border-gray-200 pt-6">
              <SubscriptionGate requiredPlan="PREMIUM">
                <CompanionManager travelId={id} />
              </SubscriptionGate>
            </div>
          )}

          {/* Health Sub-Pages Quick Navigation */}
          {isEditMode && id && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Health Analysis
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  {
                    label: t.vaccinations ?? "Vaccinations",
                    icon: Syringe,
                    path: `vaccinations`,
                    color: "text-red-600 bg-red-50 border-red-200",
                  },
                  {
                    label: t.riskManagement ?? "Risk Management",
                    icon: AlertTriangle,
                    path: `riskmanagement`,
                    color: "text-orange-600 bg-orange-50 border-orange-200",
                  },
                  {
                    label: t.recommendations ?? "Recommendations",
                    icon: CheckCircle,
                    path: `recommendations`,
                    color: "text-primary-600 bg-primary-50 border-primary-200",
                  },
                  {
                    label: t.readiness ?? "Readiness",
                    icon: CheckCircle,
                    path: `readiness`,
                    color: "text-green-600 bg-green-50 border-green-200",
                  },
                  {
                    label: t.healthcareFacilities ?? "Healthcare Facilities",
                    icon: Building2,
                    path: `facilities`,
                    color: "text-purple-600 bg-purple-50 border-purple-200",
                  },
                ].map(({ label, icon: Icon, path, color }) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => navigate(`/traveler/travel/${id}/${path}`)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium hover:shadow-sm transition-all ${color}`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Health Requirements Section */}
          {formData.country && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Syringe className="h-5 w-5 text-red-600" />
                {t.healthRecommendations}
              </h3>

              <NudgeBanner
                variant="info"
                live={false}
                icon={<AlertTriangle className="h-5 w-5" />}
                title={t.healthInformationNotice}
                className="mb-4"
              >
                {t.healthRequirementsDescription}
                {healthData && (
                  <p className="text-xs mt-2 opacity-80">
                    {t.lastUpdated || "Last updated"}:{" "}
                    {fmt.date(healthData.lastUpdated)} • {t.source || "Source"}:{" "}
                    {healthData.source}
                  </p>
                )}
              </NudgeBanner>

              {/* Loading State */}
              {loadingHealth && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <p className="ml-3 text-gray-600">{t.loading}</p>
                </div>
              )}

              {/* Vaccination Requirements */}
              {!loadingHealth && healthData && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    {t.requiredVaccinations}
                  </h4>
                  {healthData.vaccinations.length > 0 ? (
                    <div className="space-y-2">
                      {healthData.vaccinations.map((vaccine, index) => (
                        <div
                          key={index}
                          className={`flex items-start gap-3 p-3 rounded-lg border ${
                            vaccine.required
                              ? "bg-red-50 border-red-200"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <Syringe
                            className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                              vaccine.required
                                ? "text-red-600"
                                : "text-gray-600"
                            }`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">
                                {vaccine.name}
                              </p>
                              {vaccine.required && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded">
                                  {t.required || "Required"}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {vaccine.timing}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                      <p>
                        {t.noVaccinationsRequired ||
                          "No specific vaccinations required"}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Health Warnings */}
              {!loadingHealth && healthData && (
                <div className="space-y-3 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    {t.healthWarnings}
                  </h4>
                  {healthData.healthWarnings.length > 0 ? (
                    <div className="space-y-2">
                      {healthData.healthWarnings.map((warning, index) => (
                        <div
                          key={index}
                          className={`flex items-start gap-3 p-3 rounded-lg border ${
                            warning.level === "high"
                              ? "bg-red-50 border-red-200"
                              : warning.level === "medium"
                                ? "bg-yellow-50 border-yellow-200"
                                : "bg-primary-50 border-primary-200"
                          }`}
                        >
                          <AlertTriangle
                            className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                              warning.level === "high"
                                ? "text-red-600"
                                : warning.level === "medium"
                                  ? "text-yellow-600"
                                  : "text-primary-600"
                            }`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-0.5 text-xs font-medium rounded uppercase ${
                                  warning.level === "high"
                                    ? "bg-red-100 text-red-800"
                                    : warning.level === "medium"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-primary-100 text-primary-800"
                                }`}
                              >
                                {warning.level} {t.risk || "risk"}
                              </span>
                              <p className="text-sm text-gray-600 capitalize">
                                {warning.type}
                              </p>
                            </div>
                            <p className="text-sm text-gray-900 mt-1">
                              {warning.message}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                      <p>
                        {t.noHealthWarnings || "No specific health warnings"}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Recommendations */}
              {!loadingHealth &&
                healthData &&
                healthData.recommendations.length > 0 && (
                  <div className="space-y-3 mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      {t.recommendations || "Recommendations"}
                    </h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <ul className="space-y-2">
                        {healthData.recommendations.map((rec, index) => (
                          <li
                            key={index}
                            className="flex items-start gap-2 text-sm text-green-900"
                          >
                            <span className="text-green-600 mt-0.5">✓</span>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

              {/* Not entitled: the offer, not an empty section */}
              {!loadingHealth && (knownNotEntitled || healthPaywalled) && (
                <TripHealthUpsell country={formData.country} />
              )}

              {/* No Data Available */}
              {!loadingHealth &&
                !healthData &&
                !knownNotEntitled &&
                !healthPaywalled && (
                  <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                    <Syringe className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p>{t.vaccinationInfoLoading}</p>
                  </div>
                )}
            </div>
          )}

          {/* Insurers' offers for this trip (REQ-025). Below the health
              requirements, and absent entirely when there are none. */}
          {isEditMode && id && <PartnerOffersSection tripId={id} />}
        </div>

        {/* Form Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            {t.cancel}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {saving ? t.saving : t.save}
          </button>
        </div>
      </form>
    </div>
  );
};
